import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Presentation, MessageSquareQuote, MousePointerClick, Minimize2, Maximize2, Clock } from "lucide-react";
import { DEMO_STEPS, DEMO_TOTAL_MIN } from "./demoSteps";

// Floating presenter panel shown on an engagement workspace when ?demo=1. Drives the workspace tab
// for each step and keeps the step in the URL so a refresh (or a projector reconnect) lands on the same step.
export function DemoGuide({ eng, setActive }) {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const stepIdx = Math.min(DEMO_STEPS.length - 1, Math.max(0, parseInt(params.get("step") || "0", 10) || 0));
  const step = DEMO_STEPS[stepIdx];
  const [min, setMin] = useState(false);

  useEffect(() => { setActive(step.tab); window.scrollTo({ top: 0, behavior: "smooth" }); }, [step.tab, stepIdx, setActive]);

  const go = (i) => { const p = new URLSearchParams(params); p.set("demo", "1"); p.set("step", String(i)); setParams(p, { replace: true }); };
  const exit = () => nav("/demo");

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight" && stepIdx < DEMO_STEPS.length - 1) go(stepIdx + 1);
      if (e.key === "ArrowLeft" && stepIdx > 0) go(stepIdx - 1);
      if (e.key === "Escape") exit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx]);

  const elapsed = DEMO_STEPS.slice(0, stepIdx).reduce((n, s) => n + s.minutes, 0);

  if (min) {
    return (
      <button type="button" onClick={() => setMin(false)} className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-[#0F1825] text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] shadow-[0_18px_40px_-16px_rgba(15,24,37,0.6)]" data-testid="demo-guide-restore">
        <Presentation size={12} /> Demo · step {stepIdx + 1}/{DEMO_STEPS.length} <Maximize2 size={11} className="opacity-70" />
      </button>
    );
  }

  return (
    <aside className="fixed bottom-5 right-5 z-50 w-[min(420px,calc(100vw-2.5rem))] bg-[#0F1825] text-white shadow-[0_24px_60px_-20px_rgba(15,24,37,0.7)] border border-white/10" data-testid="demo-guide" aria-label="Guided demo">
      <header className="px-5 pt-4 pb-3 flex items-start gap-3 border-b border-white/10">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#E8B59A]">{step.eyebrow}</div>
          <h2 className="font-serif text-[1.25rem] leading-snug mt-1" data-testid="demo-step-title">{step.title}</h2>
          <div className="mt-1.5 font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/50 inline-flex items-center gap-1.5"><Clock size={10} /> {elapsed}–{elapsed + step.minutes} min of ~{DEMO_TOTAL_MIN} · {eng?.brand}</div>
        </div>
        <button type="button" onClick={() => setMin(true)} className="text-white/50 hover:text-white" title="Minimise" data-testid="demo-guide-minimise"><Minimize2 size={14} /></button>
        <button type="button" onClick={exit} className="text-white/50 hover:text-white" title="Exit demo (Esc)" data-testid="demo-exit"><X size={15} /></button>
      </header>
      <div className="px-5 py-4 space-y-4 max-h-[46vh] overflow-y-auto">
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/50 inline-flex items-center gap-1.5 mb-2"><MessageSquareQuote size={11} /> Say</div>
          <ul className="space-y-2" data-testid="demo-say">
            {step.say.map((s, i) => <li key={i} className="text-[13px] leading-relaxed text-white/90 pl-3 border-l border-[#C84C21]">{s}</li>)}
          </ul>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-white/50 inline-flex items-center gap-1.5 mb-2"><MousePointerClick size={11} /> Show</div>
          <p className="text-[13px] leading-relaxed text-white/80" data-testid="demo-show">{step.show}</p>
        </div>
      </div>
      <footer className="px-5 py-3 border-t border-white/10 flex items-center gap-3">
        <button type="button" disabled={stepIdx === 0} onClick={() => go(stepIdx - 1)} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-white disabled:opacity-30" data-testid="demo-prev"><ChevronLeft size={13} /> Back</button>
        <ol className="flex-1 flex items-center justify-center gap-1" data-testid="demo-dots">
          {DEMO_STEPS.map((s, i) => (
            <li key={s.key}>
              <button type="button" onClick={() => go(i)} title={s.title} className={`block h-1.5 transition-all ${i === stepIdx ? "w-6 bg-[#C84C21]" : i < stepIdx ? "w-2 bg-white/70" : "w-2 bg-white/25"}`} data-testid={`demo-dot-${i}`} aria-label={`Step ${i + 1}`} />
            </li>
          ))}
        </ol>
        {stepIdx < DEMO_STEPS.length - 1 ? (
          <button type="button" onClick={() => go(stepIdx + 1)} className="inline-flex items-center gap-1 bg-[#C84C21] hover:bg-[#B6431C] text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]" data-testid="demo-next">Next <ChevronRight size={13} /></button>
        ) : (
          <button type="button" onClick={exit} className="inline-flex items-center gap-1 bg-white text-[#0F1825] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em]" data-testid="demo-finish">Finish</button>
        )}
      </footer>
    </aside>
  );
}
